import { render } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { HostHome } from './screens/HostHome.js';
import { Landing } from './screens/Landing.js';
import { SignIn } from './components/SignIn.js';
import * as auth from './lib/auth.js';
import { PreJoin } from './screens/PreJoin.js';
import { Stage } from './screens/Stage.js';
import { useRoom } from './lib/useRoom.js';
import type { IncomingReaction } from './lib/reactions.js';
import * as api from './lib/api.js';
import type { PublicRoom, Presence } from './lib/api.js';
import './app.css';

/** `/r/<roomId>` — one shareable link per show, which is how people arrive. */
const roomFromPath = (): string | null => {
  const match = window.location.pathname.match(/^\/r\/([a-z0-9][a-z0-9-]{2,63})\/?$/);
  return match?.[1] ?? null;
};

/**
 * `/host` — the installed app's start_url, and the two hosts' whole interface.
 *
 * A separate path rather than a flag on the landing page so the home-screen
 * icon opens straight into it: an installed PWA launches at its start_url, and
 * a host should never see the audience's "enter a room code" screen.
 */
const isHostPath = (): boolean => /^\/host\/?$/.test(window.location.pathname);

/**
 * `/join?invite=…` — where a standing invite lands.
 *
 * A rolling link cannot name its room in the path: the room is whichever show is
 * running when it is opened, and only the server can say which that is. So this
 * path has no room, and the redeem response supplies one.
 */
const isJoinPath = (): boolean => /^\/join\/?$/.test(window.location.pathname);

function App() {
  const [roomId, setRoomId] = useState<string | null>(roomFromPath);
  const [hostView, setHostView] = useState(isHostPath);
  const [identity, setIdentity] = useState<api.HostIdentity | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [room, setRoom] = useState<PublicRoom | null>(null);
  const [presence, setPresence] = useState<Presence | null>(null);
  const [capabilities, setCapabilities] = useState<api.Capability[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [joining, setJoining] = useState(isJoinPath);
  const [notice, setNotice] = useState<string | null>(null);
  const [reactions, setReactions] = useState<IncomingReaction[]>([]);

  const isModerator = capabilities.includes('room:moderate');
  const canPresent = capabilities.includes('room:present');
  const canBroadcast = capabilities.includes('room:broadcast');

  const pushReaction = useCallback((reaction: IncomingReaction) => {
    // Keep a short tail only — the layer animates the newest and the rest are
    // already gone from screen. An unbounded array is a slow leak in a long show.
    setReactions((current) => [...current, reaction].slice(-40));
  }, []);

  const {
    connect, toggleMic, toggleCamera, toggleScreen, leave, enableAudio,
    room: liveRoom, ...state
  } = useRoom(pushReaction);
  const [mySub, setMySub] = useState('');

  /*
   * Establish the session before anything else so the doorway can show the
   * visitor their own avatar while they type their name.
   *
   * NOT on the host path. Both this and the enrollment effect below run on
   * mount, and both cause the server to write a session cookie — this one an
   * anonymous session, that one an elevated host session. Whichever response
   * lands last wins, so on a slow network the anonymous cookie could overwrite
   * the host's and the app would answer "Only hosts can start a show" to a host
   * who had just enrolled. Observed: two devices enrolled seconds apart, one
   * worked and one did not.
   *
   * The host path has its own identity call and does not render the doorway, so
   * skipping this costs nothing there.
   */
  useEffect(() => {
    if (hostView) return;
    api
      .getSession()
      .then((session) => setMySub(session.sub))
      .catch(() => {
        /* the join attempt will surface it properly */
      });
  }, [hostView]);

  /*
   * Host enrollment and identity.
   *
   * An `?enroll=` link is redeemed BEFORE identity is read, since redeeming is
   * what makes the answer "yes, a host". The token is then stripped from the
   * URL: it is a credential, and leaving it there puts it in history, in a
   * screenshot, and in whatever the next share sheet picks up.
   */
  /*
   * Auth material is handled on ANY path, not just /host.
   *
   * GoTrue redirects to GOTRUE_SITE_URL — the site ROOT — not to the page that
   * started the sign-in. Gating this on hostView meant the tokens landed on `/`,
   * where nothing looked for them: the account was created server-side, the
   * login succeeded, and the user was shown the login screen again. Both hosts
   * hit it and it looked like their device's fault.
   */
  useEffect(() => {
    const returning = auth.pendingAuthRedirect();
    if (!hostView && !returning) return;
    let alive = true;
    void (async () => {
      /*
       * Tokens first. GoTrue returns them in the URL fragment, and hostMe() has
       * to be able to send one — otherwise the very first call after signing in
       * is anonymous and the screen flashes "not a host" before correcting
       * itself a moment later.
       */
      // AWAITED: it may exchange a PKCE code over the network, and hostMe()
      // below has to send the resulting token. Firing and forgetting here is the
      // difference between landing on the host screen and bouncing back to login.
      const captured = await auth.captureRedirect();
      if (captured && !isHostPath()) {
        // Signed in from the root: go where the sign-in was heading.
        window.history.replaceState({}, '', '/host');
        if (alive) setHostView(true);
      }

      const params = new URLSearchParams(window.location.search);
      const token = params.get('enroll');
      if (token) {
        setEnrolling(true);
        try {
          await api.enrollHost(token);
        } catch {
          /* falls through to a non-host identity, which explains itself */
        } finally {
          window.history.replaceState({}, '', '/host');
          if (alive) setEnrolling(false);
        }
      }
      try {
        const me = await api.hostMe();
        if (alive) setIdentity(me);
      } catch {
        if (alive) setIdentity(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [hostView]);

  /*
   * A lapsed account session has to be visible.
   *
   * Behind it sits the legacy enrolled-device cookie, which still grants host
   * rights for a year — so the app kept working and quietly filed shows under
   * the old anonymous subject. Saying so is the whole fix; the fallback itself
   * stays, because it is the break-glass for a night when GoTrue is down.
   */
  useEffect(() => auth.onSessionLost(() => setSignedOut(true)), []);

  /* Back/forward should work: this is a normal web page with a shareable URL,
     not an app that traps you. */
  useEffect(() => {
    const onPop = () => setRoomId(roomFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((id: string | null) => {
    window.history.pushState({}, '', id ? `/r/${id}` : '/');
    setRoomId(id);
  }, []);

  /* Load the room before asking for a name, so the door can say which show it
     is — and 404 early rather than after someone has typed. */
  /*
   * Standing invite: redeem FIRST, then adopt the room the server resolved.
   *
   * Everything below this keys off `roomId`, which a rolling link does not carry
   * — so this runs before that and fills it in. It also rewrites the address to
   * the real room, both to drop the token out of history and so a reload behaves
   * like any other room link.
   */
  useEffect(() => {
    if (!isJoinPath()) return;
    const invite = new URLSearchParams(window.location.search).get('invite');
    if (!invite) {
      setLoadError('That link is missing its invite.');
      setJoining(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const redeemed = await api.redeemInvite(invite);
        if (!alive) return;
        window.history.replaceState({}, '', `/r/${redeemed.room.id}`);
        setRoomId(redeemed.room.id);
        setNotice(
          redeemed.role === 'moderator'
            ? 'You are hosting this room.'
            : 'You have been given the stage.',
        );
      } catch (error) {
        if (!alive) return;
        // Strip the token even on failure: it stays a capability whether or not
        // this attempt worked.
        window.history.replaceState({}, '', '/join');
        setLoadError(
          error instanceof api.ApiError
            ? error.message
            : 'That invite link could not be used.',
        );
      } finally {
        if (alive) setJoining(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!roomId) {
      setRoom(null);
      setLoadError(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        /*
         * An invite in the URL is redeemed BEFORE the room is loaded, so the
         * door already reflects the role it granted — a host should not see the
         * audience view for a beat and then have it change under them.
         *
         * The token is stripped from the address bar immediately: it is a
         * capability, and leaving it there puts it in history, in screenshots,
         * and in the Referer of anything the page later loads.
         */
        const invite = new URLSearchParams(window.location.search).get('invite');
        if (invite) {
          window.history.replaceState({}, '', `/r/${roomId}`);
          try {
            const redeemed = await api.redeemInvite(invite);
            if (alive) {
              setNotice(
                redeemed.role === 'moderator'
                  ? 'You are hosting this room.'
                  : 'You have been given the stage.',
              );
            }
          } catch (error) {
            if (alive) {
              setNotice(
                error instanceof api.ApiError
                  ? error.message
                  : 'That invite link could not be used.',
              );
            }
          }
        }

        const [loaded, seen] = await Promise.all([
          api.getRoom(roomId),
          api.getPresence(roomId).catch(() => null),
        ]);
        if (!alive) return;
        /*
         * An old show's room. Real, loadable, and empty — so say so, rather than
         * letting it look like tonight's show with nobody in it.
         */
        if (loaded.endedShow && loaded.todayRoomId) {
          setNotice(
            `This was ${loaded.room.title}. Tonight's show is somewhere else — ` +
              'open the link you were sent, or ask for a fresh one.',
          );
        }
        setRoom(loaded.room);
        setCapabilities(loaded.capabilities);
        setPresence(seen);
        document.title = `${loaded.room.title} · Waves`;
      } catch (error) {
        if (!alive) return;
        setLoadError(
          error instanceof api.ApiError && error.status === 404
            ? 'That room does not exist.'
            : 'Could not load that room.',
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, [roomId]);

  const handleJoin = useCallback(
    async (name: string) => {
      if (!roomId) return;
      setConnecting(true);
      setLoadError(null);
      try {
        // The name is a profile update, not an identity claim — the server
        // keeps the subject it already assigned us.
        await api.setName(name);
        const grant = await api.join(roomId);
        setRoom(grant.room);
        setCapabilities(grant.capabilities);
        await connect(grant.url, grant.token, grant.capabilities.includes('room:speak'));
      } catch (error) {
        setLoadError(
          error instanceof Error ? error.message : 'Could not join. Try again.',
        );
      } finally {
        setConnecting(false);
      }
    },
    [roomId, connect],
  );

  const handleLeave = useCallback(async () => {
    await leave();
    setReactions([]);
    setRoom(null);
    setCapabilities([]);
    // A host returns to their own home screen, not to the audience landing —
    // leaving a show is "done for now", not "log out".
    if (hostView) {
      setRoomId(null);
      window.history.replaceState({}, '', '/host');
      return;
    }
    navigate(null);
  }, [leave, navigate, hostView]);

  /** A host tapped Go live: the grant is already in hand, so connect. */
  const handleHostLive = useCallback(
    async (
      grant: Awaited<ReturnType<typeof api.startTodaysShow>>,
      micTrack: MediaStreamTrack,
    ) => {
      setRoom(grant.room);
      setCapabilities(grant.capabilities);
      setRoomId(grant.room.id);
      try {
        // The microphone opened under the tap — see primeMicrophone for why it
        // is carried here rather than re-acquired.
        await connect(grant.url, grant.token, true, { micTrack });
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Could not connect.');
      }
    },
    [connect],
  );

  const inRoom = state.phase === 'connected' || state.phase === 'reconnecting';

  /*
   * The host app. Rendered instead of the audience flow, not alongside it —
   * once connected it falls through to the same Stage everyone else sees,
   * because a host in a show IS just a participant with more capabilities.
   */
  if (hostView && !inRoom) {
    if (enrolling || !identity) {
      return (
        <Shell>
          <main class="shell">
            <section class="cell cell--hero">
              <p class="t-foot">{enrolling ? 'Setting up this device…' : 'Loading…'}</p>
            </section>
          </main>
        </Shell>
      );
    }
    /*
     * Signed out mid-use: say so, and stop.
     *
     * `identity.host` may well still be true here — the legacy enrolled-device
     * cookie underneath grants it for a year — so falling through would hand back
     * a working host screen under the WRONG subject, which is exactly how a
     * night's show ended up filed to an anonymous identity. Sign-in is the only
     * offer.
     */
    if (signedOut) {
      return (
        <Shell>
          <main class="host">
            <p class="notice notice--bad">
              You were signed out. Sign in again before going live, or tonight's
              show will be filed under your old identity.
            </p>
            <SignIn
              todayRoomId={identity.roomId}
              onGuest={() => {
                window.location.assign(`/r/${identity.roomId}`);
              }}
            />
          </main>
        </Shell>
      );
    }

    if (!identity.host) {
      /*
       * Not a host — which now means "not signed in as one", not "this phone was
       * never enrolled". The screen therefore offers the action that fixes it
       * rather than an instruction about a link that may not exist.
       */
      return (
        <Shell>
          <SignIn
            todayRoomId={identity.roomId}
            onGuest={() => {
              window.location.assign(`/r/${identity.roomId}`);
            }}
          />
        </Shell>
      );
    }

    return (
      <Shell>
        {loadError ? <p class="notice notice--bad">{loadError}</p> : null}
        <HostHome identity={identity} onLive={handleHostLive} />
      </Shell>
    );
  }

  /*
   * A standing link has no room until the server resolves one, so it must not
   * fall through to the audience landing page — that would flash "enter a room
   * code" at a guest who followed a perfectly good link, and would swallow the
   * error entirely if redeeming failed.
   */
  if (joining) {
    return (
      <Shell>
        <main class="shell">
          <section class="cell cell--hero">
            <p class="t-foot">Joining tonight's show…</p>
          </section>
        </main>
      </Shell>
    );
  }

  if (!roomId && isJoinPath() && loadError) {
    return (
      <Shell>
        <main class="shell">
          <section class="cell cell--hero stack">
            <h1 class="t-h1">{loadError}</h1>
            <p class="t-foot">Ask whoever invited you for a fresh link.</p>
          </section>
        </main>
      </Shell>
    );
  }

  if (!roomId) return <Shell><Landing onOpen={navigate} /></Shell>;

  if (loadError && !room) {
    return (
      <Shell>
        <main class="shell">
          <section class="cell cell--hero stack">
            <h1 class="t-h1">{loadError}</h1>
            <p class="t-foot">Check the link, or ask whoever invited you for a fresh one.</p>
            <div class="actions">
              <button class="primary" onClick={() => navigate(null)}>
                Try another room
              </button>
            </div>
          </section>
        </main>
      </Shell>
    );
  }

  if (!room) {
    return (
      <Shell>
        <main class="shell">
          <section class="cell cell--hero">
            <p class="t-foot">Opening the room…</p>
          </section>
        </main>
      </Shell>
    );
  }

  if (inRoom) {
    return (
      <Stage
        room={room}
        state={state}
        liveRoom={liveRoom}
        isModerator={isModerator}
        canPresent={canPresent}
        canBroadcast={canBroadcast}
        onToggleMic={toggleMic}
        onToggleCamera={toggleCamera}
        onToggleScreen={toggleScreen}
        onLeave={handleLeave}
        onEnableAudio={enableAudio}
        reactions={reactions}
        pushReaction={pushReaction}
      />
    );
  }

  return (
    <Shell>
      <PreJoin
          room={room}
          presence={presence}
          mySub={mySub}
          onJoin={handleJoin}
          connecting={connecting}
          error={loadError ?? state.error}
          notice={notice}
        />
    </Shell>
  );
}

function Shell({ children }: { children: preact.ComponentChildren }) {
  return (
    <>
      <header class="topbar">
        <a class="brand" href="/">
          <span class="brand__mark">Waves</span>
          <span class="brand__sub">of Africa</span>
        </a>
      </header>
      {children}
    </>
  );
}

const root = document.getElementById('root');
if (root) render(<App />, root);
